import { motion } from 'framer-motion';
import { Phone, ArrowRight, Star } from 'lucide-react';
import { businessInfo } from '../data/menuData';

const FiveStars = () => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(
      <Star key={i} size={16} className="fill-bakery-primary text-bakery-primary" />
    );
  }
  return <div className="flex">{stars}</div>;
};

const ServiceBadges = () => {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <span className="px-4 py-2 bg-bakery-muted dark:bg-gray-700 rounded-full text-sm text-bakery-text dark:text-bakery-bg font-medium">
        Dine-in
      </span>
      <span className="px-4 py-2 bg-bakery-muted dark:bg-gray-700 rounded-full text-sm text-bakery-text dark:text-bakery-bg font-medium">
        Takeaway
      </span>
      <span className="px-4 py-2 bg-bakery-muted dark:bg-gray-700 rounded-full text-sm text-bakery-text dark:text-bakery-bg font-medium">
        Delivery
      </span>
    </div>
  );
};

// WhatsApp icon component
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const HeroSection = () => {
  const scrollToMenu = () => {
    const element = document.querySelector('#menu');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const whatsappLink = `https://wa.me/${businessInfo.whatsapp}?text=Hello%20MKD%20Bakery!%20I%20would%20like%20to%20place%20an%20order.`;

  return (
    <section id="hero" className="relative min-h-[90vh] bg-bakery-bg dark:bg-gray-900 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            {/* Rating badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-bakery-accent/50 dark:bg-gray-700 px-4 py-2 rounded-full mb-6"
            >
              <FiveStars />
              <span className="text-bakery-text dark:text-bakery-bg font-medium text-sm">{businessInfo.rating} Rated</span>
            </motion.div>

            {/* Main heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-bakery-text dark:text-bakery-bg leading-tight mb-6"
            >
              Freshly Baked{' '}
              <span className="text-bakery-primary">Happiness</span>{' '}
              Every Day
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg md:text-xl text-bakery-text/80 dark:text-bakery-bg/80 leading-relaxed mb-8 max-w-lg"
            >
              Welcome to MKD Bakery, your trusted local bakery in Tegeta, Dar es Salaam. 
              Discover fresh cakes, pastries, and breads made with love.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center gap-2 bg-bakery-text dark:bg-bakery-primary text-bakery-bg px-8 py-4 rounded-full font-medium btn-lift text-lg"
                data-testid="hero-call-btn"
              >
                <Phone size={20} />
                Call to Order
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-full font-medium btn-lift text-lg"
                data-testid="hero-whatsapp-btn"
              >
                <WhatsAppIcon size={20} />
                WhatsApp
              </a>
            </motion.div>

            {/* View Menu link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4"
            >
              <button
                onClick={scrollToMenu}
                className="flex items-center gap-2 text-bakery-text dark:text-bakery-bg hover:text-bakery-primary transition-colors duration-300 font-medium"
                data-testid="hero-menu-btn"
              >
                View Menu
                <ArrowRight size={18} />
              </button>
            </motion.div>

            {/* Order via Msosi app */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-6 text-bakery-text/70 dark:text-bakery-bg/70 text-sm"
            >
              Or order via <span className="font-semibold text-bakery-primary">Msosi App</span> for delivery
            </motion.p>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <ServiceBadges />
            </motion.div>
          </motion.div>

          {/* Right Images Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="grid grid-cols-2 gap-4">
              {/* Main large image - MKD Bakery wedding cake */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="col-span-2 rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/hxyd2guo_1.PNG"
                  alt="MKD Bakery elegant white wedding cake"
                  className="w-full h-64 md:h-80 object-cover hero-image"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  data-testid="hero-main-image"
                />
              </motion.div>

              {/* Smaller images - MKD products */}
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="rounded-2xl overflow-hidden shadow-xl"
              >
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/r990s0yr_2.webp"
                  alt="MKD Bakery birthday cake gift set"
                  className="w-full h-40 md:h-48 object-cover hero-image"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03 }}
                className="rounded-2xl overflow-hidden shadow-xl"
              >
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/1fi0lhaq_3.webp"
                  alt="MKD Bakery red birthday cake"
                  className="w-full h-40 md:h-48 object-cover hero-image"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="absolute hidden lg:block -bottom-4 right-8 bg-white dark:bg-gray-800 px-6 py-4 rounded-2xl shadow-xl"
            >
              <p className="font-accent text-2xl text-bakery-primary">Made with Love</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-bakery-accent/30 dark:bg-bakery-primary/20 rounded-full blur-2xl" />
      <div className="absolute bottom-20 right-20 w-32 h-32 bg-bakery-primary/20 rounded-full blur-3xl" />
    </section>
  );
};

export default HeroSection;
